import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Trash2, AlertTriangle, RefreshCw, ImagePlus, FileText } from "lucide-react";

const inp = "h-9 w-full px-2.5 rounded-lg bg-white border border-slate-200 text-[11px] text-slate-700 focus:outline-none focus:border-[#C4738A]";
const PINK = "#C4738A";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd);
  return data.url;
}

const DOC_LABEL = { purchase_note: "Nota Pembelian", customer_order: "Pesanan Pelanggan", product_list: "Daftar Barang" };
const DUP_OPTIONS = [
  { id: "new", label: "Buat produk baru" },
  { id: "skip", label: "Gunakan produk lama (lewati)" },
  { id: "merge", label: "Tambahkan variasi ke produk lama" },
  { id: "add_stock", label: "Tambahkan stok ke produk lama" },
];

function Switch({ on, onClick, tid }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={onClick} data-testid={tid}
      className={`w-10 h-[22px] rounded-full relative transition-colors flex-shrink-0 ${on ? "bg-[#C4738A]" : "bg-slate-300"}`}>
      <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-[3px]"}`} />
    </button>
  );
}

export default function AdminProductAI() {
  const nav = useNavigate();
  const [files, setFiles] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [resolutions, setResolutions] = useState({});
  const [saving, setSaving] = useState(false);
  const [zoomUrl, setZoomUrl] = useState(null);
  const [photoModal, setPhotoModal] = useState(null);
  const [autoPricing, setAutoPricing] = useState(true);
  const [autoSku, setAutoSku] = useState(true);
  const [autoBarcode, setAutoBarcode] = useState(true);
  const fileRef = useRef(null);
  const imgRefs = useRef({});

  const load = () => api.get("/admin/ai/drafts").then((r) => setDrafts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/ai/settings").then((r) => { setAutoPricing(r.data.auto_pricing !== false); setAutoSku(r.data.auto_sku !== false); setAutoBarcode(r.data.auto_barcode !== false); }).catch(() => {});
  }, []);

  const toggleSetting = async (key, v, setter, msg) => {
    setter(v);
    try {
      const { data: cur } = await api.get("/admin/ai/settings");
      await api.put("/admin/ai/settings", { enabled: cur.enabled, doc_model: cur.doc_model, image_model: cur.image_model, monthly_limit: cur.monthly_limit, auto_pricing: cur.auto_pricing !== false, auto_sku: cur.auto_sku !== false, auto_barcode: cur.auto_barcode !== false, [key]: v });
      toast.success(msg);
    } catch { toast.error("Gagal menyimpan pengaturan"); }
  };

  const isValid = (d) => (d.missing || []).length === 0 && d.doc_type !== "customer_order";
  const validCount = drafts.filter(isValid).length;

  const generate = async () => {
    if (!files.length) return toast.error("Pilih file nota/invoice/daftar barang dulu");
    setParsing(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/ai/parse-products", fd, { timeout: 120000 });
      toast.success(`AI membuat ${data.count} draft produk`);
      setFiles([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI gagal membaca dokumen");
    } finally { setParsing(false); }
  };

  const setD = (id, k, v) => setDrafts((ds) => ds.map((d) => (d.id === id ? { ...d, [k]: v } : d)));
  const setDV = (id, vi, k, v) => setDrafts((ds) => ds.map((d) => (d.id === id ? { ...d, variants: d.variants.map((x, i) => (i === vi ? { ...x, [k]: v } : x)) } : d)));

  const pickDraftImage = async (id, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setD(id, "image", await uploadImage(f)); toast.success("Foto terunggah"); }
    catch { toast.error("Gagal mengunggah foto"); }
  };

  const genBarcode = async (id, vi) => {
    try {
      const { data } = await api.post("/admin/barcode/generate");
      vi === null ? setD(id, "barcode", data.barcode) : setDV(id, vi, "barcode", data.barcode);
    } catch { toast.error("Gagal generate barcode"); }
  };

  const resolveCategory = async (d) => {
    const t = (d.category_text ?? "").trim();
    if (!t) return "";
    const found = cats.find((c) => c.name.toLowerCase() === t.toLowerCase() || c.id === t.toLowerCase());
    if (found) return found.id;
    const { data } = await api.post("/admin/categories", { name: t });
    setCats((cs) => [...cs, data]);
    return data.id;
  };

  const persistDraft = async (d) => {
    const catId = await resolveCategory(d);
    await api.put(`/admin/ai/drafts/${d.id}`, {
      name: d.name, description: d.description || "", category: catId,
      brand: d.brand || "", collection: d.collection || "",
      sku: d.sku || "", barcode: d.barcode || "",
      cost_price: d.cost_price === "" || d.cost_price == null ? null : parseInt(d.cost_price),
      price: d.price === "" || d.price == null ? null : parseInt(d.price),
      stock: parseInt(d.stock) || 0, image: d.image || null,
      variants: (d.variants || []).map((v) => ({
        color: v.color, size: v.size, sku: v.sku || "", barcode: v.barcode || "",
        stock: parseInt(v.stock) || 0,
        cost_price: v.cost_price === "" || v.cost_price == null ? null : parseInt(v.cost_price),
        price: v.price === "" || v.price == null ? null : parseInt(v.price),
      })),
    });
  };

  const save = async (ids) => {
    if (!ids.length) return toast.error("Tidak ada draft terpilih");
    setSaving(true);
    try {
      for (const d of drafts.filter((x) => ids.includes(x.id))) {
        await persistDraft(d);
      }
      const { data } = await api.post("/admin/ai/drafts/save", { ids, resolutions });
      const parts = [];
      if (data.saved) parts.push(`${data.saved} produk baru (draft)`);
      if (data.merged) parts.push(`${data.merged} variasi ditambahkan`);
      if (data.stocked) parts.push(`${data.stocked} stok ditambah`);
      if (data.skipped) parts.push(`${data.skipped} dilewati`);
      toast.success(parts.join(" • ") || "Selesai");
      (data.errors || []).slice(0, 3).forEach((e) => toast.error(`${e.name}: ${e.error}`));
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const openPhoto = async (d, regen = false) => {
    setPhotoModal((m) => ({ id: d.id, loading: true, url: regen ? null : (m?.id === d.id ? m.url : null) }));
    try {
      const { data } = await api.post(`/admin/ai/drafts/${d.id}/photo-preview`, {}, { timeout: 180000 });
      setPhotoModal({ id: d.id, loading: false, url: data.image });
    } catch (e) {
      setPhotoModal(null);
      toast.error(e?.response?.data?.detail || "Gagal generate foto AI");
    }
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });
  const del = async (id) => {
    try { await api.delete(`/admin/ai/drafts/${id}`); setDrafts((ds) => ds.filter((d) => d.id !== id)); toast.success("Draft dihapus"); }
    catch { toast.error("Gagal menghapus draft"); }
  };

  const catName = (d) => d.category_text ?? (cats.find((c) => c.id === d.category)?.name || d.category_suggestion || "");

  return (
    <div data-testid="ai-page" className="space-y-5 pb-24">
      <div className="flex items-center gap-3">
        <button onClick={() => nav("/admin/products")} className="w-9 h-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:border-[#C4738A] transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Tambah Produk dengan AI</h1>
          <p className="text-xs text-slate-400">Unggah nota pembelian / invoice supplier / daftar barang — AI membuat draft produk untuk direview</p>
        </div>
      </div>

      {/* Upload */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <div className="flex items-start gap-4 flex-wrap">
          <button onClick={() => fileRef.current?.click()} data-testid="ai-upload-zone"
            className="w-44 h-44 rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#C4738A]/60 hover:bg-rose-50/30 transition-colors flex flex-col items-center justify-center gap-2 flex-shrink-0">
            <Sparkles className="w-6 h-6 text-[#C4738A]" />
            <span className="text-xs font-semibold text-slate-600">Upload di sini</span>
            <span className="text-[9px] text-slate-400 px-4 text-center leading-tight">JPG, PNG, PDF, Excel, CSV — satu nota bisa jadi banyak produk</span>
          </button>
          <input ref={fileRef} data-testid="ai-file-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.xlsx,.xls,.csv" className="hidden"
            onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          {files.map((f, i) => (
            <div key={i} className="relative group flex-shrink-0">
              {/\.(jpe?g|png|webp)$/i.test(f.name) ? (
                <button onClick={() => setZoomUrl(URL.createObjectURL(f))} title="Klik untuk perbesar" className="block">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-44 h-44 rounded-2xl object-cover border border-slate-200 group-hover:border-[#C4738A]/60 transition-colors" />
                </button>
              ) : (
                <div className="w-44 h-44 rounded-2xl bg-slate-100 border border-slate-200 flex flex-col items-center justify-center gap-2 px-3">
                  <FileText className="w-8 h-8 text-slate-400" />
                  <span className="text-[10px] text-slate-500 text-center leading-tight break-all">{f.name}</span>
                </div>
              )}
              <button onClick={() => setFiles(files.filter((_, x) => x !== i))}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-800 text-white items-center justify-center text-xs hidden group-hover:flex shadow">×</button>
              <div className="text-[9px] text-slate-400 mt-1.5 w-44 truncate text-center">{f.name}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-x-5 gap-y-2 flex-wrap">
            <p className="text-[10px] text-slate-400">Klik thumbnail untuk melihat foto ukuran penuh.</p>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Switch tid="ai-auto-pricing" on={autoPricing} onClick={() => toggleSetting("auto_pricing", !autoPricing, setAutoPricing, autoPricing ? "Harga jual otomatis: NONAKTIF — isi manual" : "Harga jual otomatis per kategori: AKTIF")} />
              <span className="text-[11px] font-semibold text-slate-700">Harga jual otomatis</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Switch tid="ai-auto-sku" on={autoSku} onClick={() => toggleSetting("auto_sku", !autoSku, setAutoSku, autoSku ? "Generate SKU otomatis: NONAKTIF" : "Generate SKU otomatis: AKTIF")} />
              <span className="text-[11px] font-semibold text-slate-700">Generate SKU otomatis</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Switch tid="ai-auto-barcode" on={autoBarcode} onClick={() => toggleSetting("auto_barcode", !autoBarcode, setAutoBarcode, autoBarcode ? "Generate barcode otomatis: NONAKTIF" : "Generate barcode otomatis: AKTIF")} />
              <span className="text-[11px] font-semibold text-slate-700">Generate barcode otomatis</span>
            </label>
            <span className="text-[10px] text-slate-400">— saklar aktif: harga, SKU & barcode semua variasi langsung terisi begitu draft AI muncul.</span>
            <button type="button" data-testid="ai-pricing-settings-link" onClick={() => nav("/admin/settings/ai")}
              className="text-[10px] font-semibold text-[#C4738A] hover:underline">Atur profit & aturan harga →</button>
          </div>
          <button data-testid="ai-generate-btn" onClick={generate} disabled={parsing || !files.length}
            className="h-11 px-6 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-2 transition-colors disabled:opacity-50">
            {parsing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {parsing ? "AI sedang membaca dokumen..." : "Generate Produk dengan AI"}
          </button>
        </div>
      </div>
      {/* lightbox foto */}
      {zoomUrl && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-6" onClick={() => setZoomUrl(null)} data-testid="ai-zoom">
          <img src={zoomUrl} alt="Pratinjau" className="max-w-full max-h-full rounded-2xl shadow-2xl object-contain" />
        </div>
      )}
      {/* modal Generate Foto AI */}
      {photoModal && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" data-testid="ai-photo-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3">
            <h3 className="font-display font-bold text-sm text-slate-900">Generate Foto AI</h3>
            <div className="w-full aspect-square rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
              {photoModal.loading
                ? <div className="flex flex-col items-center gap-2 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin text-[#C4738A]" /><span className="text-[11px]">AI sedang membuat foto produk...</span></div>
                : photoModal.url && <img src={photoModal.url} alt="Foto AI" className="w-full h-full object-cover" />}
            </div>
            <p className="text-[10px] text-slate-400">Bila draft punya foto referensi, AI mempertahankan desain, warna, motif, dan bentuk produk asli.</p>
            <div className="flex gap-2">
              <button data-testid="ai-photo-regen" onClick={() => openPhoto(drafts.find((x) => x.id === photoModal.id), true)} disabled={photoModal.loading}
                className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-[#C4738A] disabled:opacity-50">
                <RefreshCw className="w-3.5 h-3.5" /> Generate Ulang
              </button>
              <button data-testid="ai-photo-use" onClick={() => { setD(photoModal.id, "image", photoModal.url); setPhotoModal(null); toast.success("Foto AI dipasang ke draft"); }} disabled={photoModal.loading || !photoModal.url}
                className="h-10 px-5 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex-1 transition-colors disabled:opacity-50">Gunakan Foto Ini</button>
              <button onClick={() => setPhotoModal(null)} className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600">Tutup</button>
            </div>
          </div>
        </div>
      )}

      {/* Daftar draft */}
      {drafts.length > 0 && (
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-sm text-slate-900">Hasil AI — {drafts.length} draft menunggu review</h2>
          <span className="text-[11px] text-slate-400">{validCount} siap disimpan • produk tersimpan sebagai <b>Draft</b> (tidak otomatis aktif)</span>
        </div>
      )}
      <div className="space-y-4">
        {drafts.map((d) => (
          <div key={d.id} data-testid={`ai-draft-${d.id}`} className={`bg-white rounded-2xl border overflow-hidden ${isValid(d) ? "border-slate-100" : "border-amber-200"}`}>
            {/* banner duplikat / pesanan pelanggan */}
            {d.doc_type === "customer_order" && (
              <div className="px-5 py-2 bg-amber-50 text-amber-700 text-[11px] font-medium flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Terdeteksi sebagai pesanan pelanggan ({DOC_LABEL[d.doc_type]}) — tidak dianggap pembelian stok/harga modal dan tidak bisa disimpan.
              </div>
            )}
            {d.dup && (
              <div data-testid={`ai-dup-${d.id}`} className="px-5 py-2 bg-sky-50 text-sky-700 text-[11px] flex items-center gap-2 flex-wrap">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Mirip produk lama (<b>{d.dup.product_name || d.dup.detail}</b> — kecocokan {d.dup.kind}):</span>
                <select data-testid={`ai-res-${d.id}`} value={resolutions[d.id] || "new"} onChange={(e) => setResolutions((r) => ({ ...r, [d.id]: e.target.value }))}
                  className="h-7 px-2 rounded-lg border border-sky-200 bg-white text-[11px] text-slate-700 focus:outline-none">
                  {DUP_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            )}
            <div className="p-5 flex gap-4">
              {/* kiri: ceklis + foto */}
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <input type="checkbox" data-testid={`ai-select-${d.id}`} checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)}
                  className="w-4 h-4 rounded border-slate-300 accent-[#C4738A]" />
                <button onClick={() => imgRefs.current[d.id]?.click()} title="Upload Foto"
                  className="w-20 h-20 rounded-xl border border-slate-200 overflow-hidden bg-slate-50 hover:border-[#C4738A] transition-colors flex items-center justify-center">
                  {d.image ? <img src={d.image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 text-slate-300" />}
                </button>
                <input ref={(el) => (imgRefs.current[d.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => pickDraftImage(d.id, e)} />
                <span className="text-[9px] text-slate-400">{DOC_LABEL[d.doc_type] || "Dokumen"}</span>
              </div>
              {/* tengah: field utama */}
              <div className="flex-1 min-w-0 space-y-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  {(d.missing || []).length > 0
                    ? <span data-testid={`ai-missing-${d.id}`} className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2.5 py-1">Perlu Dilengkapi: {(d.missing || []).join(", ")}</span>
                    : <span className="text-[10px] font-bold bg-emerald-100 text-emerald-600 rounded-full px-2.5 py-1">Lengkap — siap disimpan</span>}
                  {d.price_from_rule && <span className="text-[10px] font-semibold bg-violet-100 text-violet-600 rounded-full px-2.5 py-1">Harga jual dari Aturan Harga</span>}
                </div>
                <input data-testid={`ai-name-${d.id}`} value={d.name} onChange={(e) => setD(d.id, "name", e.target.value)}
                  className="w-full text-sm font-bold text-slate-900 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#C4738A] focus:outline-none py-1" />
                <textarea value={d.description || ""} onChange={(e) => setD(d.id, "description", e.target.value)} rows={2} placeholder="Deskripsi produk (wajib diisi)..."
                  className="w-full text-[11px] text-slate-600 bg-transparent border border-slate-100 rounded-lg px-2.5 py-2 focus:outline-none focus:border-[#C4738A]" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[9px] font-semibold text-slate-400 mb-0.5">Kategori *</div>
                    <input list="ai-cats" value={catName(d)} onChange={(e) => setD(d.id, "category_text", e.target.value)} placeholder="cth. Atasan" className={inp} />
                  </div>
                  <div>
                    <div className="text-[9px] font-semibold text-slate-400 mb-0.5">SKU Induk</div>
                    <div className="flex gap-1"><input value={d.sku || ""} onChange={(e) => setD(d.id, "sku", e.target.value)} placeholder="otomatis" className={inp} /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <div className="text-[9px] font-semibold text-slate-400 mb-0.5">Harga Modal *</div>
                    <input type="number" value={d.cost_price ?? ""} onChange={(e) => setD(d.id, "cost_price", e.target.value)} placeholder="dari nota" className={inp} />
                  </div>
                  <div>
                    <div className="text-[9px] font-semibold text-slate-400 mb-0.5">Harga Jual *</div>
                    <input type="number" value={d.price ?? ""} onChange={(e) => setD(d.id, "price", e.target.value)} placeholder="aturan/manual" className={inp} />
                  </div>
                  {!d.variants?.length && (
                    <>
                      <div><div className="text-[9px] font-semibold text-slate-400 mb-0.5">Stok (pcs)</div><input type="number" value={d.stock} onChange={(e) => setD(d.id, "stock", e.target.value)} className={inp} /></div>
                      <div>
                        <div className="text-[9px] font-semibold text-slate-400 mb-0.5">Barcode</div>
                        <div className="flex gap-1">
                          <input value={d.barcode || ""} onChange={(e) => setD(d.id, "barcode", e.target.value)} placeholder="manual/otomatis" className={inp} />
                          <button onClick={() => genBarcode(d.id, null)} title="Generate barcode" className="w-9 h-9 flex-shrink-0 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-[#C4738A] hover:text-white transition-colors"><RefreshCw className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* variasi */}
                {d.variants?.length > 0 && (
                  <div className="rounded-xl border border-slate-100 overflow-hidden overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="text-[9px] uppercase tracking-wide text-slate-400 bg-rose-50/60 border-b border-rose-100/60">
                          <th className="text-left px-3 py-2 font-semibold">Warna / Ukuran</th>
                          <th className="text-left px-2 py-2 font-semibold">SKU</th>
                          <th className="text-left px-2 py-2 font-semibold">Barcode</th>
                          <th className="text-center px-2 py-2 font-semibold w-16">Pcs</th>
                          <th className="text-right px-2 py-2 font-semibold w-24">Modal</th>
                          <th className="text-right px-3 py-2 font-semibold w-24">Jual</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {d.variants.map((v, vi) => (
                          <tr key={vi}>
                            <td className="px-3 py-1.5 text-[11px] font-medium text-slate-700">{v.color} / {v.size}</td>
                            <td className="px-2 py-1.5"><input value={v.sku || ""} onChange={(e) => setDV(d.id, vi, "sku", e.target.value)} placeholder="otomatis" className={`${inp} font-mono`} /></td>
                            <td className="px-2 py-1.5">
                              <div className="flex gap-1 items-center">
                                <input value={v.barcode || ""} onChange={(e) => setDV(d.id, vi, "barcode", e.target.value)} placeholder="otomatis" className={`${inp} font-mono`} />
                                <button onClick={() => genBarcode(d.id, vi)} className="w-7 h-7 flex-shrink-0 rounded-md text-slate-300 hover:bg-[#C4738A] hover:text-white flex items-center justify-center transition-colors"><RefreshCw className="w-3 h-3" /></button>
                              </div>
                            </td>
                            <td className="px-2 py-1.5"><input type="number" value={v.stock} onChange={(e) => setDV(d.id, vi, "stock", e.target.value)} className={`${inp} text-center`} /></td>
                            <td className="px-2 py-1.5"><input type="number" value={v.cost_price ?? ""} onChange={(e) => setDV(d.id, vi, "cost_price", e.target.value)} className={`${inp} text-right`} /></td>
                            <td className="px-3 py-1.5"><input type="number" value={v.price ?? ""} onChange={(e) => setDV(d.id, vi, "price", e.target.value)} className={`${inp} text-right`} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {/* kanan: aksi */}
              <div className="flex flex-col gap-1.5 flex-shrink-0 w-44">
                <button data-testid={`ai-photo-${d.id}`} onClick={() => openPhoto(d)}
                  className="h-8 px-3 rounded-lg border border-violet-200 text-violet-500 text-[10px] font-semibold flex items-center gap-1.5 hover:bg-violet-50 transition-colors">
                  <Sparkles className="w-3 h-3" /> Generate Foto AI
                </button>
                <button data-testid={`ai-del-${d.id}`} onClick={() => del(d.id)}
                  className="h-8 px-3 rounded-lg border border-slate-200 text-slate-400 text-[10px] font-semibold flex items-center gap-1.5 hover:border-red-300 hover:text-red-500 transition-colors">
                  <Trash2 className="w-3 h-3" /> Hapus Draft
                </button>
                {d.price != null && <div className="text-right mt-1"><div className="text-[9px] text-slate-400">Harga jual</div><div className="text-sm font-bold text-slate-900">{formatIDR(parseInt(d.price) || 0)}</div></div>}
                {d.cost_price != null && <div className="text-right"><div className="text-[9px] text-slate-400">Modal</div><div className="text-[11px] font-semibold text-slate-600">{formatIDR(parseInt(d.cost_price) || 0)}</div></div>}
              </div>
            </div>
          </div>
        ))}
      </div>
      <datalist id="ai-cats">{cats.map((c) => <option key={c.id} value={c.name} />)}</datalist>

      {/* bar simpan massal */}
      {drafts.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-2 z-40">
          <span className="text-[11px] text-slate-400 mr-auto">{selected.size} terpilih • {validCount} valid</span>
          <button data-testid="ai-save-selected" onClick={() => save([...selected])} disabled={saving || !selected.size}
            className="h-10 px-5 rounded-xl border border-[#C4738A]/50 text-[#C4738A] text-xs font-semibold hover:bg-[#C4738A]/5 transition-colors disabled:opacity-50">
            Simpan Produk Terpilih ({selected.size})
          </button>
          <button data-testid="ai-save-all" onClick={() => save(drafts.filter(isValid).map((d) => d.id))} disabled={saving || !validCount}
            className="h-10 px-6 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold transition-colors disabled:opacity-50">
            {saving ? "Menyimpan..." : `Simpan Semua Produk Valid (${validCount})`}
          </button>
        </div>
      )}
    </div>
  );
}
